import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import agent from '../api/axios';

interface PaymentSettings {
  id: string;
  allowCard: boolean;
  allowAchDebit: boolean;
  allowPayPal: boolean;
  allowCashApp: boolean;
  allowLink: boolean;
}

export default function PaymentSettingsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const data = await agent.PaymentSettings.get();
      setSettings(data);
    } catch (error: any) {
      console.error('Failed to load settings', error);
      enqueueSnackbar('Failed to load payment settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggle = (field: keyof Omit<PaymentSettings, 'id'>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [field]: !settings[field],
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    // Validate at least one method is enabled
    if (
      !settings.allowCard &&
      !settings.allowAchDebit &&
      !settings.allowPayPal &&
      !settings.allowCashApp &&
      !settings.allowLink
    ) {
      enqueueSnackbar('At least one payment method must be enabled', {
        variant: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await agent.PaymentSettings.update(settings);
      enqueueSnackbar('Payment settings saved successfully', {
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Failed to save settings', error);
      enqueueSnackbar('Failed to save payment settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return <Alert severity="error">Failed to load payment settings</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
        }}
      >
        <SettingsIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4">Payment Settings</Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Registration Payment Methods
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select which payment methods customers can use during registration.
            At least one method must be enabled. Note: These are the payment
            methods supported by Stripe SetupIntent for saving payment
            information.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowCard}
                  onChange={() => handleToggle('allowCard')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Credit/Debit Card</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept major credit and debit cards (Visa, Mastercard, Amex,
                    etc.)
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowAchDebit}
                  onChange={() => handleToggle('allowAchDebit')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">ACH Direct Debit</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept ACH bank account payments (US only)
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowPayPal}
                  onChange={() => handleToggle('allowPayPal')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">PayPal</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept PayPal payments
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowCashApp}
                  onChange={() => handleToggle('allowCashApp')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Cash App Pay</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept Cash App payments (US only)
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowLink}
                  onChange={() => handleToggle('allowLink')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Link by Stripe</Typography>
                  <Typography variant="caption" color="text.secondary">
                    One-click checkout with Link (includes Apple Pay & Google
                    Pay support)
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
